/**
 * useQuickAddDraft — manages a persistent draft task for Quick Add.
 *
 * The draft lives at `parentPath: "root/#quick-add"` with `status: Hidden`
 * so it never appears in any view. When saved, the task is moved to the
 * chosen container with `status: Unfinished`.
 *
 * If the user closes Quick Add without saving, the draft persists in Dexie
 * so all edits (title, notes via TipTap/Yjs, checklist, tags, dates) are
 * preserved for the next time Quick Add is opened.
 */

import { useState, useEffect, useCallback } from "react"
import { db, ObjectType, ObjectStatus } from "@/db/schema"
import type { DbObject } from "@/db/schema"
import { createTaskDefaults } from "@/shared/types/task"
import {
  getInsertIndex,
  resolveContainerRealmId,
} from "@/db/operations"
import { useLiveQueryImmediate } from "@/db/hooks"
import { reopenContainerIfClosed } from "@totodo/operations"
import { moveQuickAddDraft } from "./moveQuickAddDraft"

const DRAFT_PARENT_PATH = "root/#quick-add"

export function useQuickAddDraft(open: boolean) {
  const [ready, setReady] = useState(false)

  // Eagerly ensure a draft row exists on mount — so it's ready before the
  // modal opens.  When `open` transitions to true, the draft is already in
  // IndexedDB and the live query returns it immediately (no "Loading…" flash).
  useEffect(() => {
    let cancelled = false

    async function ensureDraft() {
      const existing = await db.totodo
        .where("parentPath")
        .equals(DRAFT_PARENT_PATH)
        .filter((r) => r.isDeleted === 0)
        .first()

      if (cancelled) return

      if (existing) {
        // Repair legacy drafts (created with Hidden status)
        if (existing.status === ObjectStatus.Hidden) {
          await db.totodo.update(existing.id!, {
            status: ObjectStatus.Unfinished,
          })
        }
      } else {
        const id = crypto.randomUUID()
        const now = new Date().toISOString()
        await db.totodo.add(
          createTaskDefaults({
            id,
            parentPath: DRAFT_PARENT_PATH,
            status: ObjectStatus.Unfinished,
            createdAt: now,
            createdBy: "local",
          }),
        )
      }

      if (!cancelled) setReady(true)
    }

    ensureDraft()
    return () => {
      cancelled = true
    }
  }, []) // Mount-only — draft is pre-warmed before user taps Quick Add

  // After a save, the draft row is moved away. Reset `ready` so the live
  // query returns null, then re-ensure a fresh draft for the next open.
  useEffect(() => {
    if (!open || ready) return
    // `ready` is false after a save — create a fresh draft
    let cancelled = false
    async function refreshDraft() {
      const existing = await db.totodo
        .where("parentPath")
        .equals(DRAFT_PARENT_PATH)
        .filter((r) => r.isDeleted === 0)
        .first()
      if (cancelled) return
      if (!existing) {
        const id = crypto.randomUUID()
        const now = new Date().toISOString()
        await db.totodo.add(
          createTaskDefaults({
            id,
            parentPath: DRAFT_PARENT_PATH,
            status: ObjectStatus.Unfinished,
            createdAt: now,
            createdBy: "local",
          }),
        )
      }
      if (!cancelled) setReady(true)
    }
    refreshDraft()
    return () => { cancelled = true }
  }, [open, ready])

  // Live query keeps the draft prop fresh as sub-components edit it.
  // Always active with a stable cache key — no gating on `ready` — so the
  // observable is reused across renders and never resets to null.
  const [draft] = useLiveQueryImmediate(
    async () => {
      return (
        (await db.totodo
          .where("parentPath")
          .equals(DRAFT_PARENT_PATH)
          .filter((r) => r.isDeleted === 0)
          .first()) ?? null
      )
    },
    ["quick-add-draft"],
    null,
  )

  // Move draft to the target container and promote to a real task
  const save = useCallback(
    async (containerId: string | null) => {
      if (!draft?.id) return false

      const parentPath = containerId
        ? `root/${containerId}`
        : "root/#inbox"
      const index = await getInsertIndex(
        parentPath,
        "top",
        ObjectType.Task,
      )
      const realmId = await resolveContainerRealmId(parentPath)

      const updates: Partial<DbObject> & { parentPath: string; realmId: string } = {
        parentPath,
        index,
        realmId,
        updatedAt: new Date().toISOString(),
      }

      // Ensure the task is visible when moved to a real container
      if (draft.status !== ObjectStatus.Completed && draft.status !== ObjectStatus.Canceled) {
        updates.status = ObjectStatus.Unfinished
      }

      // An open to-do saved into a closed project / list reopens it (first)
      if (updates.status === ObjectStatus.Unfinished) await reopenContainerIfClosed(parentPath)

      // With its checklist: rows added in quick-add live under the draft's path
      await moveQuickAddDraft(draft, updates)
      setReady(false) // next open creates a fresh draft
      return true
    },
    [draft],
  )

  // Reset the draft — delete existing and create a fresh one
  const resetDraft = useCallback(async () => {
    const existing = await db.totodo
      .where("parentPath")
      .equals(DRAFT_PARENT_PATH)
      .filter((r) => r.isDeleted === 0)
      .first()

    if (existing?.id) {
      await db.totodo.delete(existing.id)
    }

    const id = crypto.randomUUID()
    const ts = new Date().toISOString()
    await db.totodo.add(
      createTaskDefaults({
        id,
        parentPath: DRAFT_PARENT_PATH,
        status: ObjectStatus.Unfinished,
        createdAt: ts,
        createdBy: "local",
      }),
    )
    setReady(true)
  }, [])

  return { draft, save, resetDraft }
}
